import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain } from 'lucide-react';
const QUESTIONS = [{
  id: 1,
  question: 'Cuando estás a punto de entrar a una situación social que te genera tensión, ¿qué hace tu cuerpo SIN que tú lo decidas?',
  options: ['Siento el corazón acelerado y me falta el aire, como si algo malo fuera a pasar', 'Me sudan las manos, se me seca la boca y siento que mi cara se pone roja', 'Mi estómago se cierra, siento náuseas y ganas de inventar cualquier excusa para no ir', 'Mi mente se va en blanco y pierdo toda la seguridad que tenía antes de llegar']
}, {
  id: 2,
  question: 'Cuando termina un evento o conversación importante, ¿qué pasa en tu cabeza durante las horas siguientes?',
  options: ["Replay mental: repaso cada cosa que dije buscando el momento donde la 'regué'", 'Me quedo atrapado/a imaginando lo que los demás deben estar pensando o diciendo de mí', 'No puedo dormir porque mi mente sigue procesando la situación aunque yo quiera parar', 'Me siento agotado/a mentalmente como si hubiera corrido un maratón, aunque solo estuve en una reunión de una hora']
}, {
  id: 3,
  question: '¿En cuál de estas situaciones sientes que tu ansiedad social ya te ha costado algo importante?',
  options: ['En el trabajo: me quedé callado/a con buenas ideas y vi cómo otros subieron de puesto por decir lo mismo que yo pensaba', 'En mis relaciones: he cancelado planes y evitado conocer gente nueva por miedo a quedar mal', 'En mi autoestima: cada vez que evito una situación social me siento más pequeño/a y menos capaz', 'En mi energía: anticipar una situación social me consume más energía que el evento mismo']
}, {
  id: 4,
  question: 'Cuando sabes que una situación social te va a generar ansiedad, ¿cuál es tu respuesta más honesta?',
  options: ['Cancelo, pospongo o invento una excusa que suene creíble', 'Voy pero me escudo con el celular para parecer ocupado/a y no tener que hablar', 'Lo aguanto sufriendo, cuento los minutos para irme y llego a casa destruido/a', 'Antes de ir ya me estoy diciendo que lo voy a arruinar y que no sé comportarme']
}, {
  id: 5,
  question: "¿Cuántas veces has intentado 'calmarte' diciéndote que no hay razón para estar nervioso/a?",
  options: ['Todo el tiempo y no funciona. Mi cuerpo sigue en pánico aunque sé que no hay peligro real', 'Lo intento pero dura segundos. El nerviosismo regresa más fuerte que antes', 'Ya dejé de intentarlo porque me frustra darme cuenta de que no puedo controlarme', 'A veces funciona pero el esfuerzo me deja agotado/a para el resto del día']
}, {
  id: 6,
  question: 'Si tu ansiedad social sigue igual en los próximos 2 años, ¿cuál de estas realidades te genera más miedo?',
  options: ['Seguir viendo cómo mi vida social, amorosa y profesional se hace más pequeña', 'Que mis personas cercanas noten que algo no está bien y no saber cómo explicarlo', 'Que evitar situaciones sociales se vuelva tan normal que ya no recuerde cómo era antes', 'Darme cuenta demasiado tarde de todas las oportunidades que perdí por el miedo']
}];
const LOADING_TEXTS = ['Analizando tu perfil de reactividad...', 'Identificando patrones del sistema nervioso...', 'Calculando tu nivel de cortisol social...', 'Preparando tu protocolo personalizado...'];
export function QuizSection({
  onComplete
}: {
  onComplete: () => void;
}) {
  const [step, setStep] = useState(0);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [status, setStatus] = useState<'quiz' | 'analyzing' | 'results'>('quiz');
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const advanceToNext = useCallback(() => {
    if (step < QUESTIONS.length - 1) {
      setStep(prev => prev + 1);
      setSelectedOption(null);
    } else {
      setStatus('analyzing');
    }
  }, [step]);
  const handleOptionSelect = (option: string) => {
    if (isTransitioning) return;
    setSelectedOption(option);
    setIsTransitioning(true);
    setTimeout(() => {
      advanceToNext();
      setIsTransitioning(false);
    }, 600);
  };
  useEffect(() => {
    if (status === 'analyzing') {
      const interval = setInterval(() => {
        setLoadingTextIndex(prev => {
          if (prev < LOADING_TEXTS.length - 1) return prev + 1;
          clearInterval(interval);
          setTimeout(() => setStatus('results'), 1000);
          return prev;
        });
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [status]);
  if (status === 'analyzing') {
    return <div className="fixed inset-0 z-50 bg-navy flex flex-col items-center justify-center p-4" data-id="element-168">
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30" data-id="element-169">
          <div className="absolute top-[20%] left-[20%] w-64 h-64 rounded-full bg-sage blur-3xl animate-float" data-id="element-170" />
          <div className="absolute bottom-[20%] right-[20%] w-64 h-64 rounded-full bg-yellow blur-3xl animate-float" style={{
          animationDelay: '1s'
        }} data-id="element-171" />
        </div>
        <div className="relative z-10 flex flex-col items-center text-center" data-id="element-172">
          <div className="relative w-32 h-32 flex items-center justify-center mb-8" data-id="element-173">
            <div className="absolute inset-0 border-4 border-sage/20 rounded-full" data-id="element-174"></div>
            <div className="absolute inset-0 border-4 border-sage rounded-full border-t-transparent animate-spin" data-id="element-175"></div>
            <Brain className="w-12 h-12 text-yellow animate-pulse" data-id="element-176" />
          </div>
          <AnimatePresence mode="wait" data-id="element-177">
            <motion.h2 key={loadingTextIndex} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="text-2xl md:text-3xl font-heading font-bold text-offwhite" data-id="element-178">
              {LOADING_TEXTS[loadingTextIndex]}
            </motion.h2>
          </AnimatePresence>
        </div>
      </div>;
  }
  if (status === 'results') {
    return <section className="min-h-screen bg-navy px-4 flex items-center justify-center" data-id="element-179">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="max-w-2xl mx-auto bg-cream rounded-[24px] shadow-2xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-180">
          <div className="inline-block bg-red-600 text-white font-bold text-sm px-4 py-1.5 rounded-full mb-6 tracking-wide" data-id="element-181">
            ⚠️ DIAGNÓSTICO: TU SISTEMA NERVIOSO NECESITA ATENCIÓN
          </div>

          <h2 className="text-3xl md:text-4xl font-heading font-bold text-charcoal mb-6 leading-tight" data-id="element-182">
            Tu sistema nervioso está trabajando en tu contra, no por tu culpa.
          </h2>

          <p className="text-charcoal/80 text-lg mb-10 leading-relaxed" data-id="element-183">
            Tus respuestas indican que tu Nervio Vago está atrapado en una
            respuesta crónica de "lucha o huida". Esto explica por qué la fuerza
            de voluntad o el pensamiento positivo no te han funcionado: tu
            cuerpo está reaccionando a un nivel biológico antes de que tu mente
            lógica pueda intervenir.
          </p>

          <div className="w-full bg-charcoal/10 h-3 rounded-full mb-10 overflow-hidden relative" data-id="element-184">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: '82%'
          }} transition={{
            duration: 1.5,
            ease: 'easeOut'
          }} className="absolute top-0 left-0 h-full bg-gradient-to-r from-red-600 to-terracotta rounded-full" data-id="element-185" />
          </div>

          <button onClick={onComplete} className="w-full bg-red-600 hover:bg-red-700 text-white font-heading font-bold text-lg py-5 px-8 rounded-full shadow-lg transition-all animate-heartbeat" data-id="element-186">
            QUIERO LA SOLUCIÓN DE RAÍZ →
          </button>
        </motion.div>
      </section>;
  }
  const progress = (step + 1) / QUESTIONS.length * 100;
  return <section className="py-20 bg-navy px-4 min-h-screen flex items-center justify-center" id="quiz-section" data-id="element-187">
      <div className="w-full max-w-3xl" data-id="element-188">
        <div className="bg-cream rounded-[24px] shadow-2xl overflow-hidden relative" data-id="element-189">
          <div className="w-full h-1.5 bg-sage/20" data-id="element-190">
            <motion.div className="h-full bg-sage" initial={{
            width: `${step / QUESTIONS.length * 100}%`
          }} animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.3
          }} data-id="element-191" />
          </div>

          <div className="p-6 md:p-10" data-id="element-192">
            <div className="mb-8 text-center" data-id="element-193">
              <span className="inline-block bg-terracotta/10 text-terracotta font-bold text-xs px-3 py-1 rounded-full uppercase tracking-wider mb-4" data-id="element-194">
                Pregunta {step + 1} de {QUESTIONS.length}
              </span>
              <AnimatePresence mode="wait" data-id="element-195">
                <motion.h3 key={step} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="text-2xl md:text-3xl font-heading font-bold text-charcoal leading-tight" data-id="element-196">
                  {QUESTIONS[step].question}
                </motion.h3>
              </AnimatePresence>
            </div>

            <div className="space-y-3" data-id="element-197">
              <AnimatePresence mode="wait" data-id="element-198">
                <motion.div key={step} initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-3" data-id="element-199">
                  {QUESTIONS[step].options.map((option, idx) => {
                  const isSelected = selectedOption === option;
                  return <button key={idx} onClick={() => handleOptionSelect(option)} disabled={isTransitioning} className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 min-h-[56px]
                          ${isSelected ? 'bg-navy border-navy text-white border-l-[6px] border-l-terracotta' : 'bg-white border-charcoal/10 text-charcoal hover:bg-navy/5 hover:border-navy/30'}
                          ${isTransitioning && !isSelected ? 'opacity-50' : ''}`} data-id="element-200">
                        <span className="font-medium text-lg leading-snug" data-id="element-201">
                          {option}
                        </span>
                      </button>;
                })}
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="mt-6 text-center" data-id="element-202">
              <p className="text-sm font-bold text-charcoal/60" data-id="element-203">
                Tus respuestas son 100% confidenciales
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}